import { constrainValue } from 'shared/chart/shared/data';

export type BoundsOptions = Readonly<{
    top: number;
    right: number;
    bottom: number;
    left: number;
}>;

export class Bounds {
    readonly top: number;

    readonly right: number;

    readonly bottom: number;

    readonly left: number;

    readonly width: number;

    readonly height: number;

    readonly midX: number;

    readonly midY: number;

    constructor({ top, right, bottom, left }: BoundsOptions) {
        if (right < left) {
            throw new Error(
                `Right cannot be less than left (right = ${right}, left = ${left})`,
            );
        }
        if (bottom < top) {
            throw new Error(
                `Bottom cannot be less than top (bottom = ${bottom}, top = ${top})`,
            );
        }
        this.top = top;
        this.right = right;
        this.bottom = bottom;
        this.left = left;
        this.width = this.right - this.left;
        this.height = this.bottom - this.top;
        this.midX = (this.right + this.left) / 2;
        this.midY = (this.bottom + this.top) / 2;
    }

    constrainTo({ top, right, bottom, left }: Bounds) {
        return new Bounds({
            top: constrainValue(this.top, top, bottom),
            right: constrainValue(this.right, left, right),
            bottom: constrainValue(this.bottom, top, bottom),
            left: constrainValue(this.left, left, right),
        });
    }

    containsPoint({ x, y }: { x: number; y: number }, includeEdges = true) {
        return includeEdges
            ? x >= this.left && y >= this.top && x <= this.right && y <= this.bottom
            : x > this.left && y > this.top && x < this.right && y < this.bottom;
    }

    containsBounds({ top, left, bottom, right }: Bounds, includeEdges = true) {
        return (
            this.containsPoint({ x: left, y: top }, includeEdges) &&
            this.containsPoint({ x: right, y: bottom }, includeEdges)
        );
    }

    inset(amount: number): Bounds {
        const finalAmount = Math.min(this.width / 2, this.height / 2, amount);

        return new Bounds({
            left: this.left + finalAmount,
            top: this.top + finalAmount,
            right: this.right - finalAmount,
            bottom: this.bottom - finalAmount,
        });
    }

    translate(xAmount: number, yAmount: number): Bounds {
        return xAmount || yAmount
            ? new Bounds({
                  left: this.left + xAmount,
                  top: this.top + yAmount,
                  right: this.right + xAmount,
                  bottom: this.bottom + yAmount,
              })
            : this;
    }

    translateX(amount: number): Bounds {
        return amount
            ? new Bounds({
                  left: this.left + amount,
                  top: this.top,
                  right: this.right + amount,
                  bottom: this.bottom,
              })
            : this;
    }

    translateY(amount: number): Bounds {
        return amount
            ? new Bounds({
                  left: this.left,
                  top: this.top + amount,
                  right: this.right,
                  bottom: this.bottom + amount,
              })
            : this;
    }

    static fromSize(width: number, height: number) {
        return new Bounds({ top: 0, left: 0, bottom: height, right: width });
    }
}
